import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Unsubscribe } from 'firebase/firestore';
import { DirectMessageAuthService } from '../../../../core/services/direct-message-auth.service';
import { NotyfService } from '../../../../core/services/notyf.service';
import { CrmMessagingService } from '../../services/crm-messaging.service';
import { SmsSendService } from '../../services/sms-send.service';
import { SmsChatRequest, SmsChatService } from '../../services/sms-chat.service';
import { ConnectionStatus, MessagingSettingsService } from '../../services/messaging-settings.service';
import { MessagingTemplate, MessagingTemplateService } from '../../services/messaging-template.service';
import { WebRtcCallService } from '../../services/webrtc-call.service';
import { CrmMessage } from '../../models/crm-message.model';
import { CrmThread } from '../../models/crm-thread.model';

/**
 * The admin panel's floating SMS chat - same look and placement as the Call
 * console, opened from any CRM record's SMS tab via SmsChatService and
 * mounted once in AppComponent so a conversation stays open while the agent
 * moves around. Same Ringotel send/receive pipeline as SmsThreadComponent
 * (thread-for-number lookup, Firestore listeners, sms/send). Tapping a
 * message opens its delivery details.
 */
@Component({
  selector: 'app-sms-chat-console',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './sms-chat-console.component.html',
  styleUrl: './sms-chat-console.component.css'
})
export class SmsChatConsoleComponent implements OnInit, OnDestroy {
  @ViewChild('scroll') private scroll?: ElementRef<HTMLDivElement>;

  request: SmsChatRequest | null = null;
  minimized = false;

  threadId: string | null = null;
  to: string | null = null;
  thread: CrmThread | null = null;
  messages: CrmMessage[] = [];
  loading = false;
  lookupFailed = false;

  draft = '';
  sending = false;
  showSearch = false;
  search = '';
  showMenu = false;

  /** Message whose details panel is open. */
  selected: CrmMessage | null = null;

  /** Set after "Mark as unread" so the open chat doesn't immediately read
   *  it back - cleared when the chat is reopened or a message is sent. */
  holdUnread = false;
  /** New inbound arrived while scrolled up - shows the "New message" pill. */
  newBelow = false;

  templates: MessagingTemplate[] = [];
  connections: ConnectionStatus = { email: false, sms: false, chat: false };
  senderNumber = '';
  segmentSize = 160;
  costPerSegment = 0;
  /** Avoids flashing "SMS is disconnected" before settings arrive. */
  settingsReady = false;

  private subs = new Subscription();
  private detachMessages: Unsubscribe | null = null;
  private detachThread: Unsubscribe | null = null;
  private settingsLoaded = false;
  private markingRead = false;

  constructor(
    readonly sms: SmsChatService,
    private smsSendService: SmsSendService,
    private crmMessagingService: CrmMessagingService,
    private directMessageAuthService: DirectMessageAuthService,
    private messagingSettingsService: MessagingSettingsService,
    private messagingTemplateService: MessagingTemplateService,
    private webRtcCallService: WebRtcCallService,
    private notyfService: NotyfService
  ) {}

  ngOnInit (): void {
    this.subs.add(this.sms.open$.subscribe((request) => this.onOpen(request)));
  }

  onScroll (): void {
    if (this.isNearBottom()) this.newBelow = false;
  }

  jumpToLatest (): void {
    this.newBelow = false;
    this.scrollToBottom();
  }

  /** Unread inbound messages - highlighted, and what mark-read stamps. */
  get unreadMessages (): CrmMessage[] {
    return this.sms.unreadInbound(this.messages, this.unread);
  }

  isUnread (m: CrmMessage): boolean {
    return this.unreadMessages.some((u) => u.id === m.id);
  }

  markAsRead (): void {
    this.showMenu = false;
    this.holdUnread = false;
    this.markRead(true);
  }

  /** Flags the latest incoming message (or `from` onwards) unread again. */
  markAsUnread (from: CrmMessage | null = null): void {
    this.showMenu = false;
    if (!this.threadId) return;

    const inbound = this.messages.filter((m) => m.direction === 'inbound');
    const flagged = from ? this.sms.inboundFrom(this.messages, from) : inbound.slice(-1);
    if (!flagged.length) {
      this.notyfService.notify('There are no incoming messages to mark as unread.', 'error');
      return;
    }

    this.holdUnread = true;
    this.selected = null;
    this.smsSendService.markUnread(this.threadId, flagged.map((m) => m.id)).subscribe({
      next: () => this.notyfService.notify('Marked as unread.', 'success'),
      error: () => {
        this.holdUnread = false;
        this.notyfService.notify('Could not mark as unread. Please try again.', 'error');
      }
    });
  }

  ngOnDestroy (): void {
    this.subs.unsubscribe();
    this.detach();
  }

  @HostListener('document:keydown.escape')
  onEscape (): void {
    if (this.selected) {
      this.selected = null;
    } else if (this.showMenu) {
      this.showMenu = false;
    }
  }

  /** Sits left of the Call console while a call is on screen. */
  get shifted (): boolean {
    return !!this.webRtcCallService.current;
  }

  get title (): string {
    return this.request?.contactName || this.sms.formatNumber(this.to ?? this.request?.phoneNumber);
  }

  get unread (): number {
    return this.thread?.unreadCount ?? 0;
  }

  get canSend (): boolean {
    return !!this.draft.trim() && !this.sending && this.connections.sms && !!this.threadId;
  }

  get visibleMessages (): CrmMessage[] {
    const query = this.search.trim().toLowerCase();
    return query ? this.messages.filter((m) => m.text.toLowerCase().includes(query)) : this.messages;
  }

  get draftSegments (): number {
    return this.sms.segments(this.draft, this.segmentSize);
  }

  get draftCost (): number {
    return this.draftSegments * this.costPerSegment;
  }

  /** Stack consecutive messages from the same side - only the last in a
   *  run shows its time/status line, like a phone's Messages app. */
  isLastInRun (index: number): boolean {
    const list = this.visibleMessages;
    const next = list[index + 1];
    if (!next) return true;
    if (next.direction !== list[index].direction) return true;
    return this.dayKey(next) !== this.dayKey(list[index]);
  }

  isNewDay (index: number): boolean {
    const list = this.visibleMessages;
    return index === 0 || this.dayKey(list[index]) !== this.dayKey(list[index - 1]);
  }

  dayLabel (m: CrmMessage): string {
    const key = this.dayKey(m);
    const today = this.dayKeyOf(Date.now());
    const yesterday = this.dayKeyOf(Date.now() - 86400000);
    if (key === today) return 'Today';
    if (key === yesterday) return 'Yesterday';
    return new Date(this.sms.sentAtMillis(m)).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  }

  timeOf (m: CrmMessage): string {
    return new Date(this.sms.sentAtMillis(m)).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  trackById (_index: number, m: CrmMessage): string {
    return m.id;
  }

  toggleMinimized (): void {
    this.minimized = !this.minimized;
    this.syncVisible();
    if (!this.minimized) {
      this.markReadIfNeeded();
      this.jumpToLatest();
    }
  }

  close (): void {
    this.sms.close();
  }

  toggleSearch (): void {
    this.showSearch = !this.showSearch;
    this.showMenu = false;
    if (!this.showSearch) this.search = '';
  }

  select (m: CrmMessage): void {
    this.selected = this.selected?.id === m.id ? null : m;
  }

  applyTemplate (id: string): void {
    const template = this.templates.find((t) => String(t.id) === id);
    if (template) this.draft = template.body;
  }

  onComposerKeydown (event: KeyboardEvent): void {
    // Enter sends, Shift+Enter adds a new line.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  send (): void {
    const text = this.draft.trim();
    if (!this.canSend || !this.threadId || !this.to) return;

    const threadId = this.threadId;
    const to = this.to;
    this.sending = true;
    this.draft = '';
    // Replying means it's been read.
    this.holdUnread = false;
    this.markReadIfNeeded();

    this.crmMessagingService
      .addOutboundMessage(threadId, this.directMessageAuthService.myUid ?? 'admin', text, '', null, [to], [], 'sms')
      .then((messageId) => {
        this.smsSendService.send(threadId, messageId, to, text).subscribe({
          error: (err) => {
            this.crmMessagingService.markOutboundFailed(threadId, messageId);
            const reason = err?.error?.message;
            this.notyfService.notify(reason ? `Not sent: ${reason}` : 'Not sent - please try again.', 'error');
          }
        });
      })
      .catch(() => {
        this.draft = text;
        this.notyfService.notify('Could not send message. Please try again.', 'error');
      })
      .finally(() => this.sending = false);
  }

  copy (value: string | undefined): void {
    if (!value) return;
    navigator.clipboard?.writeText(value).then(
      () => this.notyfService.notify('Copied.', 'success'),
      () => undefined
    );
  }

  private onOpen (request: SmsChatRequest | null): void {
    const samePerson = !!request && !!this.request && request.phoneNumber === this.request.phoneNumber;
    this.request = request;
    this.holdUnread = false;

    if (!request) {
      this.detach();
      this.syncVisible();
      return;
    }

    this.minimized = false;
    this.selected = null;
    this.showMenu = false;
    this.syncVisible();
    if (samePerson) {
      this.markReadIfNeeded();
      this.scrollToBottom();
      return;
    }

    this.draft = '';
    this.search = '';
    this.showSearch = false;
    this.loadSettingsOnce();
    this.resolveThread(request.phoneNumber);
  }

  private loadSettingsOnce (): void {
    if (this.settingsLoaded) return;
    this.settingsLoaded = true;
    this.directMessageAuthService.ensureSignedIn().subscribe({ error: () => undefined });
    this.messagingSettingsService.get().subscribe({
      next: (settings) => {
        this.segmentSize = settings.limits.smsSegmentSize;
        this.costPerSegment = settings.limits.smsCostPerSegment;
        this.senderNumber = settings.limits.defaultSmsSender;
        this.connections = settings.connections;
        this.settingsReady = true;
      },
      // Let a later open retry instead of staying without settings.
      error: () => this.settingsLoaded = false
    });
    this.messagingTemplateService.list('sms').subscribe({
      next: (list) => this.templates = list,
      error: () => undefined
    });
  }

  private resolveThread (number: string): void {
    this.detach();
    this.loading = true;
    this.lookupFailed = false;

    this.smsSendService.threadForNumber(number.trim()).subscribe({
      next: (res) => {
        this.threadId = res.thread_id;
        this.to = res.to;
        let first = true;
        this.detachMessages = this.crmMessagingService.listMessages(res.thread_id, (messages) => {
          const previousLast = this.messages[this.messages.length - 1]?.id;
          const atBottom = this.isNearBottom();
          this.messages = messages;
          this.loading = false;
          this.sms.checkDelivery(messages);
          if (this.selected) {
            this.selected = messages.find((m) => m.id === this.selected?.id) ?? null;
          }

          const last = messages[messages.length - 1];
          const arrived = !first && !!last && last.id !== previousLast;
          if (first || atBottom || last?.direction === 'outbound') {
            this.scrollToBottom();
          } else if (arrived) {
            this.newBelow = true;
          }
          first = false;
          this.markReadIfNeeded();
        });
        this.detachThread = this.crmMessagingService.getThread(res.thread_id, (thread) => {
          this.thread = thread;
          this.markReadIfNeeded();
        });
      },
      error: () => {
        this.loading = false;
        this.lookupFailed = true;
      }
    });
  }

  /** Reading the conversation in the open chat counts as reading it -
   *  unless someone just marked it unread (holdUnread). */
  private markReadIfNeeded (): void {
    if (this.holdUnread || this.minimized || !this.request) return;
    this.markRead(false);
  }

  private markRead (notify: boolean): void {
    if (!this.threadId || !this.unread || this.markingRead) return;
    const ids = this.unreadMessages.map((m) => m.id);
    this.markingRead = true;
    this.smsSendService.markRead(this.threadId, ids).subscribe({
      next: () => {
        this.markingRead = false;
        if (notify) this.notyfService.notify('Marked as read.', 'success');
      },
      error: () => this.markingRead = false
    });
  }

  private syncVisible (): void {
    this.sms.visibleNumber = this.request && !this.minimized ? this.request.phoneNumber : null;
  }

  private isNearBottom (): boolean {
    const el = this.scroll?.nativeElement;
    return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }

  private detach (): void {
    this.detachMessages?.();
    this.detachThread?.();
    this.detachMessages = null;
    this.detachThread = null;
    this.threadId = null;
    this.to = null;
    this.thread = null;
    this.messages = [];
    this.selected = null;
  }

  private scrollToBottom (): void {
    setTimeout(() => {
      const el = this.scroll?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  private dayKey (m: CrmMessage): string {
    return this.dayKeyOf(this.sms.sentAtMillis(m));
  }

  private dayKeyOf (millis: number): string {
    const d = new Date(millis);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }
}
